/**
 * MarchAgent OIDC Authorization Code + PKCE client.
 *
 * The browser never calls Keycloak protocol endpoints directly. Authorization,
 * token exchange, refresh, and revocation are routed through MarchAgent backend.
 */

import {
  clearTokenState,
  getDevRefreshToken,
  getOAuthState,
  getPkceVerifier,
  getStaySignedIn,
  getStoredAccessToken,
  setDevAuthSession,
  setOAuthState,
  setPkceVerifier,
  type LoginSessionOptions,
} from './tokenStore';
import { isCookieAuthMode } from './cookieAuthMode';
import { decodeJwtPayload } from './jwtPayload';
import { getOidcConfiguration } from './oidcDiscovery';

export function getKeycloakIssuer(): string {
  // Deprecated for browser use. Kept for compatibility only.
  return (import.meta.env.VITE_KEYCLOAK_ISSUER ?? '').toString().trim().replace(/\/+$/, '');
}

export function getKeycloakClientId(): string {
  // Deprecated for browser token calls. The backend injects the real client_id.
  return (import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? '').toString().trim();
}

export function getOAuthRedirectUri(): string {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}/`;
}

function randomString(bytes = 32): string {
  const array = new Uint8Array(bytes);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

async function sha256Base64Url(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const bytes = new Uint8Array(digest);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function isKeycloakConfigReady(): boolean {
  // Frontend only needs MarchAgent backend. Keycloak config is private to backend.
  return true;
}

/** Start browser redirect through MarchAgent backend authorize proxy. */
export async function startKeycloakLogin(): Promise<void> {
  const config = await getOidcConfiguration();
  const authorizeUrl = config?.authorization_endpoint;
  if (!authorizeUrl) {
    throw new Error('MarchAgent auth proxy is not configured.');
  }

  const verifier = randomString(32);
  const challenge = await sha256Base64Url(verifier);
  const state = randomString(16);
  setPkceVerifier(verifier);
  setOAuthState(state);

  const params = new URLSearchParams({
    redirect_uri: getOAuthRedirectUri(),
    response_type: 'code',
    scope: 'openid profile email',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  window.location.assign(`${authorizeUrl}?${params.toString()}`);
}

export type OAuthCallbackResult =
  | { status: 'none' }
  | { status: 'success' }
  | { status: 'error'; message: string };

function applyTokenResponse(
  payload: Record<string, unknown>,
  options?: LoginSessionOptions,
): void {
  const accessToken = String(payload.access_token ?? '').trim();
  if (!accessToken) {
    throw new Error('Token response did not include access_token.');
  }
  const refreshToken = String(payload.refresh_token ?? '').trim();
  const expiresIn = Number(payload.expires_in ?? 0);
  const refreshExpiresIn = Number(payload.refresh_expires_in ?? 0);
  const expiresAt = expiresIn > 0 ? Date.now() + expiresIn * 1000 : undefined;
  const refreshExpiresAt =
    refreshExpiresIn > 0 ? Date.now() + refreshExpiresIn * 1000 : undefined;
  setDevAuthSession(
    {
      accessToken,
      refreshToken: refreshToken || undefined,
      expiresAt,
      refreshExpiresAt,
      idToken: String(payload.id_token ?? '').trim() || undefined,
      tokenType: String(payload.token_type ?? 'Bearer').trim(),
      claims: decodeJwtPayload(accessToken),
      staySignedIn: options?.staySignedIn ?? getStaySignedIn(),
    },
    options,
  );
}

/** Handle OAuth redirect callback on app load. */
export async function completeKeycloakCallbackIfPresent(): Promise<OAuthCallbackResult> {
  if (typeof window === 'undefined') return { status: 'none' };

  const url = new URL(window.location.href);
  const code = (url.searchParams.get('code') ?? '').trim();
  const state = (url.searchParams.get('state') ?? '').trim();
  const oauthError = (url.searchParams.get('error') ?? '').trim();

  if (!code && !oauthError) return { status: 'none' };

  if (oauthError) {
    const desc = (url.searchParams.get('error_description') ?? oauthError).trim();
    clearQueryOAuthParams(url);
    return { status: 'error', message: desc || 'Sign in failed.' };
  }

  const expectedState = getOAuthState();
  if (!state || state !== expectedState) {
    clearQueryOAuthParams(url);
    return { status: 'error', message: 'Invalid OAuth state. Try logging in again.' };
  }

  const verifier = getPkceVerifier();
  if (!verifier) {
    clearQueryOAuthParams(url);
    return { status: 'error', message: 'Missing PKCE verifier. Try logging in again.' };
  }

  const config = await getOidcConfiguration();
  const tokenUrl = config?.token_endpoint;
  if (!tokenUrl) {
    clearQueryOAuthParams(url);
    return { status: 'error', message: 'MarchAgent token proxy is not configured.' };
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: getOAuthRedirectUri(),
    code_verifier: verifier,
  });

  try {
    const res = await fetch(tokenUrl, {
      method: 'POST',
      // SEC-I5: ONLY in cookie mode. `credentials: 'include'` makes the browser
      // REQUIRE `Access-Control-Allow-Credentials: true` on the response, and
      // the server only sends that when cookie auth is on. Sending it
      // unconditionally made every cross-origin login fail with a CORS error --
      // the request reached the server and returned 200, the browser discarded
      // the response, and the UI reported "Invalid username or password".
      credentials: isCookieAuthMode() ? 'include' : 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      clearQueryOAuthParams(url);
      return {
        status: 'error',
        message: 'Sign in failed. Please try again.',
      };
    }
    applyTokenResponse(payload, { staySignedIn: getStaySignedIn() });
    clearQueryOAuthParams(url);
    return { status: 'success' };
  } catch (err) {
    clearQueryOAuthParams(url);
    return {
      status: 'error',
      message: err instanceof Error ? err.message : 'Token exchange failed.',
    };
  }
}

function clearQueryOAuthParams(url: URL): void {
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  url.searchParams.delete('session_state');
  url.searchParams.delete('iss');
  url.searchParams.delete('error');
  url.searchParams.delete('error_description');
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, document.title, next);
}

async function revokeToken(token: string, tokenTypeHint: 'refresh_token' | 'access_token'): Promise<void> {
  const config = await getOidcConfiguration();
  const revokeUrl = config?.revocation_endpoint;
  if (!revokeUrl || !token) return;

  const body = new URLSearchParams({
    token,
    token_type_hint: tokenTypeHint,
  });

  try {
    await fetch(revokeUrl, {
      method: 'POST',
      // SEC-I5: cookie mode only -- see the note on the token exchange above.
      credentials: isCookieAuthMode() ? 'include' : 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  } catch {
    // Best-effort revocation — local session is still cleared.
  }
}

/** Clear local session and revoke refresh/access tokens through backend proxy. */
export async function logoutKeycloakSession(): Promise<void> {
  const refreshToken = getDevRefreshToken();
  if (refreshToken) {
    await revokeToken(refreshToken, 'refresh_token');
  }
  const accessToken = getStoredAccessToken();
  if (accessToken) {
    await revokeToken(accessToken, 'access_token');
  }
  clearTokenState();
}

export function logoutDevAuth(): void {
  clearTokenState();
}

/** @deprecated use {@link logoutDevAuth} */
export function logoutKeycloakLocally(): void {
  logoutDevAuth();
}
